import {useMutation, useQueryClient} from '@tanstack/react-query';
import type {
  CompanyConfiguration,
  CreateCompanyInput,
  CreateCompanyMutationInput,
  UpdateCompanyInput,
  UpdateCompanyMutationInput,
} from 'src/core/data/api/CompanyConfiguration';
import CompanyService from 'src/services/company-service';
import {DEFAULT_LOGO_URL} from 'src/autobahn/shared/constants';
import {companyKeys} from './queries';

const companyService = new CompanyService();

const appendCacheBuster = (url: string): string => {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}v=${Date.now()}`;
};

const uploadLogo = async (customDomain: string, attachment: File): Promise<string> => {
  const logoExt = attachment.name.slice(attachment.name.lastIndexOf('.'));
  const formData = CompanyService.createUploadFormData({
    attachment,
    name: `${customDomain}${logoExt}`,
  });
  const response = await companyService.upload(formData);
  return appendCacheBuster(response.companyLogo);
};

export const useCreateCompany = () => {
  const queryClient = useQueryClient();

  return useMutation<CompanyConfiguration, Error, CreateCompanyMutationInput>({
    mutationFn: async ({attachment, customDomain, ...payload}) => {
      const logoUrl = attachment ?
        await uploadLogo(customDomain, attachment) :
        DEFAULT_LOGO_URL;
      const createInput: CreateCompanyInput = {
        logoUrl,
        customDomain,
        customerId: payload.customerId,
        label: payload.label,
        hideLoginFooter: payload.hideLoginFooter,
        theme: payload.theme,
        androidAppUrl: payload.androidAppUrl,
        iosAppUrl: payload.iosAppUrl,
        customerAreaLink: payload.customerAreaLink,
        customerAreaContact: payload.customerAreaContact,
        assistanceService: payload.assistanceService,
        monitoringCenterService: payload.monitoringCenterService,
        monitoringCenterContact: payload.monitoringCenterContact,
      };
      return companyService.createCompany(createInput);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: companyKeys.configurations()});
    },
  });
};

export const useUpdateCompany = () => {
  const queryClient = useQueryClient();

  return useMutation<CompanyConfiguration, Error, UpdateCompanyMutationInput>({
    mutationFn: async ({id, attachment, customDomain, logoUrl, ...payload}) => {
      let newLogoUrl = logoUrl;
      if (attachment) {
        newLogoUrl = await uploadLogo(customDomain, attachment);
      }
      const updateInput: UpdateCompanyInput = {
        id,
        logoUrl: newLogoUrl,
        customDomain,
        customerId: payload.customerId,
        label: payload.label,
        hideLoginFooter: payload.hideLoginFooter,
        theme: payload.theme,
        androidAppUrl: payload.androidAppUrl,
        iosAppUrl: payload.iosAppUrl,
        customerAreaLink: payload.customerAreaLink,
        customerAreaContact: payload.customerAreaContact,
        assistanceService: payload.assistanceService,
        monitoringCenterService: payload.monitoringCenterService,
        monitoringCenterContact: payload.monitoringCenterContact,
      };
      return companyService.updateCompany(updateInput);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: companyKeys.configurations()});
    },
  });
};

export const useDeleteCompany = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: (id) => companyService.deleteCompany(id),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: companyKeys.configurations()});
    },
  });
};
